import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface Report {
  id: string;
  employee_name: string;
  project_name: string;
  department_name: string;
  work_type_name: string;
  date_from: string;
  date_to: string;
  time_spent_hours: number;
  time_spent_minutes: number;
  materials?: string;
  notes?: string;
  rate_used: number;
  amount: number;
  bonus: number;
  admin_comment?: string;
  employee_personal_rate?: number;
  work_type_rate?: number;
}

const ReportsList: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Helper function to get start and end of current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const dateFrom = new Date(year, month, 1);
    const dateTo = new Date(year, month + 1, 0); // Last day of current month
    return { dateFrom, dateTo };
  };

  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const { dateFrom: initialDateFrom, dateTo: initialDateTo } = getCurrentMonthDates();
  const [filters, setFilters] = useState({
    dateFrom: initialDateFrom as Date | null,
    dateTo: initialDateTo as Date | null,
    projectIds: [] as string[],
    departmentIds: [] as string[],
    workTypeIds: [] as string[],
    employeeIds: [] as string[],
    materialIds: [] as string[],
  });
  const [projects, setProjects] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [workTypes, setWorkTypes] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [departmentMaterials, setDepartmentMaterials] = useState<any[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [editData, setEditData] = useState({ rateUsed: 0, rateType: 'personal' as 'personal' | 'workType', bonus: 0, adminComment: '' });

  useEffect(() => {
    loadReferenceData();
    loadReports();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [projectsRes, departmentsRes, workTypesRes, employeesRes, materialsRes] = await Promise.all([
        api.get('/reference/projects'),
        api.get('/reference/departments'),
        api.get('/reference/work-types'),
        user?.isAdmin ? api.get('/employees') : Promise.resolve({ data: [] }),
        api.get('/reference/materials'),
      ]);
      setProjects(projectsRes.data);
      setDepartments(departmentsRes.data);
      setWorkTypes(workTypesRes.data);
      setMaterials(materialsRes.data);
      if (user?.isAdmin) {
        setEmployees(employeesRes.data);
      }
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  // Load department materials when department filter changes
  useEffect(() => {
    const loadDepartmentMaterials = async () => {
      if (filters.departmentIds.length === 1) {
        try {
          const res = await api.get(`/reference/departments/${filters.departmentIds[0]}`);
          setDepartmentMaterials(res.data.materials || []);
        } catch (err) {
          console.error('Failed to load department materials:', err);
          setDepartmentMaterials([]);
        }
      } else {
        setDepartmentMaterials([]);
      }
    };
    loadDepartmentMaterials();
  }, [filters.departmentIds]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.dateFrom) {
        params.dateFrom = filters.dateFrom.toISOString().split('T')[0];
      }
      if (filters.dateTo) {
        params.dateTo = filters.dateTo.toISOString().split('T')[0];
      }
      if (filters.projectIds.length > 0) {
        params.projectIds = filters.projectIds;
      }
      if (filters.departmentIds.length > 0) {
        params.departmentIds = filters.departmentIds;
      }
      if (filters.workTypeIds.length > 0) {
        params.workTypeIds = filters.workTypeIds;
      }
      if (filters.employeeIds.length > 0 && user?.isAdmin) {
        params.employeeIds = filters.employeeIds;
      }
      if (filters.materialIds.length > 0) {
        // For material filter, we need to search by material names in the materials text field
        // Get material names from IDs
        const materialNames = (filters.departmentIds.length === 1 && departmentMaterials.length > 0
          ? departmentMaterials.filter((m: any) => filters.materialIds.includes(m.id))
          : materials.filter((m: any) => filters.materialIds.includes(m.id))
        ).map((m: any) => m.name);
        if (materialNames.length > 0) {
          // Search for any of the selected material names in the materials field
          params.materialSearch = materialNames.join('|');
        }
      }

      const res = await api.get('/reports', { params });
      setReports(res.data);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (report: Report) => {
    setSelectedReport(report);
    // Determine which rate is currently used
    const currentRate = report.rate_used;
    const personalRate = report.employee_personal_rate;
    const workTypeRate = report.work_type_rate;
    
    // Default to personal rate (as per MMG requirement)
    let initialRate = personalRate || workTypeRate || currentRate;
    let rateType: 'personal' | 'workType' = 'personal';
    
    // Check which rate matches current rate
    if (personalRate && Math.abs(personalRate - currentRate) < 0.01) {
      rateType = 'personal';
      initialRate = personalRate;
    } else if (workTypeRate && Math.abs(workTypeRate - currentRate) < 0.01) {
      rateType = 'workType';
      initialRate = workTypeRate;
    } else {
      // Default to personal if available, otherwise work type
      if (personalRate) {
        rateType = 'personal';
        initialRate = personalRate;
      } else if (workTypeRate) {
        rateType = 'workType';
        initialRate = workTypeRate;
      }
    }
    
    setEditData({
      rateUsed: initialRate,
      rateType,
      bonus: report.bonus,
      adminComment: report.admin_comment || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedReport) return;
    try {
      // Determine the rate to use based on selected rate type
      const rateToUse = editData.rateType === 'personal' 
        ? selectedReport.employee_personal_rate 
        : selectedReport.work_type_rate;
      
      const updateData = {
        rateUsed: rateToUse || editData.rateUsed,
        bonus: editData.bonus,
        adminComment: editData.adminComment,
      };
      
      await api.put(`/reports/${selectedReport.id}`, updateData);
      setEditDialogOpen(false);
      loadReports();
    } catch (err) {
      console.error('Failed to update report:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('reports.deleteConfirm'))) return;
    try {
      await api.delete(`/reports/${id}`);
      loadReports();
    } catch (err) {
      console.error('Failed to delete report:', err);
    }
  };

  const formatTime = (hours: number, minutes: number) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    // Parse date string (expected format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.sssZ)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If parsing fails, try to extract just the date part (YYYY-MM-DD)
      const datePart = dateString.split('T')[0].split(' ')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0].slice(-2)}`;
      }
      return dateString; // Return original if can't parse
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatMaterials = (materialsString?: string) => {
    if (!materialsString || materialsString.trim() === '') {
      return null;
    }
    // Materials are stored as: "Material Name     unit; quantity comment\n..."
    const lines = materialsString.split('\n').filter(line => line.trim() !== '');
    return lines;
  };

  const exportToExcel = () => {
    if (reports.length === 0) {
      alert(t('reports.noReportsToExport'));
      return;
    }

    // Prepare data for Excel
    const excelData = reports.map((report) => {
      const baseData: any = {
        Date: report.date_from === report.date_to 
          ? formatDate(report.date_from)
          : `${formatDate(report.date_from)} - ${formatDate(report.date_to)}`,
        Project: report.project_name,
        Department: report.department_name,
        'Work Type': report.work_type_name,
        'Time Spent': formatTime(report.time_spent_hours, report.time_spent_minutes),
        'Amount (UAH)': report.amount,
        Materials: report.materials || '',
        Notes: report.notes || '',
      };
      
      // Add admin-only fields
      if (user?.isAdmin) {
        baseData.Employee = report.employee_name;
        baseData['Rate (UAH/hr)'] = report.rate_used;
        baseData['Bonus (UAH)'] = report.bonus || 0;
        baseData['Admin Comment'] = report.admin_comment || '';
      }
      
      return baseData;
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reports');

    // Generate filename with current date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `reports_${dateStr}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          {t('reports.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          disabled={reports.length === 0}
        >
          {t('common.exportToExcel')}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label={t('report.dateFrom')}
                value={filters.dateFrom}
                onChange={(date: Date | null) => setFilters({ ...filters, dateFrom: date })}
                inputFormat="dd/MM/yy"
                renderInput={(params: any) => (
                  <TextField {...params} size="small" sx={{ width: 150 }} />
                )}
              />
              <DatePicker
                label={t('report.dateTo')}
                value={filters.dateTo}
                onChange={(date: Date | null) => setFilters({ ...filters, dateTo: date })}
                inputFormat="dd/MM/yy"
                renderInput={(params: any) => (
                  <TextField {...params} size="small" sx={{ width: 150 }} />
                )}
              />
            </Box>

            {user?.isAdmin && (
              <>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('reports.filters.projects')}
                  </Typography>
                  {projects.map((project) => (
                    <FormControlLabel
                      key={project.id}
                      control={
                        <Checkbox
                          checked={filters.projectIds.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters({
                                ...filters,
                                projectIds: [...filters.projectIds, project.id],
                              });
                            } else {
                              setFilters({
                                ...filters,
                                projectIds: filters.projectIds.filter((id) => id !== project.id),
                              });
                            }
                          }}
                        />
                      }
                      label={project.name}
                    />
                  ))}
                </Box>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      {t('reports.filters.employees')} ({filters.employeeIds.length > 0 ? filters.employeeIds.length : ''})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {employees.map((employee) => (
                        <FormControlLabel
                          key={employee.id}
                          control={
                            <Checkbox
                              checked={filters.employeeIds.includes(employee.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilters({
                                    ...filters,
                                    employeeIds: [...filters.employeeIds, employee.id],
                                  });
                                } else {
                                  setFilters({
                                    ...filters,
                                    employeeIds: filters.employeeIds.filter((id) => id !== employee.id),
                                  });
                                }
                              }}
                            />
                          }
                          label={employee.full_name}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      {t('reports.filters.materials')} ({filters.materialIds.length > 0 ? filters.materialIds.length : ''})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {(filters.departmentIds.length === 1 && departmentMaterials.length > 0
                        ? departmentMaterials
                        : materials
                      ).map((material: any) => (
                        <FormControlLabel
                          key={material.id}
                          control={
                            <Checkbox
                              checked={filters.materialIds.includes(material.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilters({
                                    ...filters,
                                    materialIds: [...filters.materialIds, material.id],
                                  });
                                } else {
                                  setFilters({
                                    ...filters,
                                    materialIds: filters.materialIds.filter((id) => id !== material.id),
                                  });
                                }
                              }}
                            />
                          }
                          label={`${material.name} (${material.unit})`}
                        />
                      ))}
                      {filters.departmentIds.length === 1 && departmentMaterials.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {t('reports.filters.noMaterialsForDepartment')}
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            <Button variant="contained" onClick={loadReports}>
              {t('reports.applyFilters')}
            </Button>
          </Box>
        </LocalizationProvider>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('reports.date')}</TableCell>
              {user?.isAdmin && <TableCell>{t('reports.employee')}</TableCell>}
              <TableCell>{t('reports.project')}</TableCell>
              <TableCell>{t('reports.department')}</TableCell>
              <TableCell>{t('reports.workType')}</TableCell>
              <TableCell>{t('reports.time')}</TableCell>
              {user?.isAdmin && <TableCell>{t('reports.rate')}</TableCell>}
              <TableCell>{t('reports.amount')}</TableCell>
              <TableCell>{t('reports.materials')}</TableCell>
              {user?.isAdmin && <TableCell>{t('reports.bonus')}</TableCell>}
              {user?.isAdmin && <TableCell>{t('reports.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.id}>
                <TableCell>
                  {report.date_from === report.date_to
                    ? formatDate(report.date_from)
                    : `${formatDate(report.date_from)} - ${formatDate(report.date_to)}`}
                </TableCell>
                {user?.isAdmin && <TableCell>{report.employee_name}</TableCell>}
                <TableCell>{report.project_name}</TableCell>
                <TableCell>{report.department_name}</TableCell>
                <TableCell>{report.work_type_name}</TableCell>
                <TableCell>
                  {formatTime(report.time_spent_hours, report.time_spent_minutes)}
                </TableCell>
                {user?.isAdmin && <TableCell>{report.rate_used} UAH/hr</TableCell>}
                <TableCell>{report.amount} UAH</TableCell>
                <TableCell>
                  {(() => {
                    const materials = formatMaterials(report.materials);
                    if (!materials || materials.length === 0) {
                      return <Typography variant="body2" color="text.secondary">-</Typography>;
                    }
                    const maxLines = 4;
                    const displayLines = materials.slice(0, maxLines);
                    const hasMore = materials.length > maxLines;
                    return (
                      <Box>
                        <Box
                          sx={{
                            maxHeight: '4.5em', // ~4 lines
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: maxLines,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {displayLines.map((line, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              sx={{
                                fontSize: '0.75rem',
                                lineHeight: '1.2',
                                mb: 0.25,
                              }}
                            >
                              {line.trim()}
                            </Typography>
                          ))}
                        </Box>
                        {hasMore && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            +{materials.length - maxLines} {t('reports.moreItems')}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
                {user?.isAdmin && <TableCell>{report.bonus} UAH</TableCell>}
                {user?.isAdmin && (
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(report)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(report.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reports.editReport')}</DialogTitle>
        <DialogContent>
          {selectedReport && (selectedReport.employee_personal_rate || selectedReport.work_type_rate) ? (
            <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
              <FormLabel component="legend">{t('reports.rateSelection')}</FormLabel>
              <RadioGroup
                value={editData.rateType}
                onChange={(e) => {
                  const newRateType = e.target.value as 'personal' | 'workType';
                  const newRate = newRateType === 'personal'
                    ? selectedReport.employee_personal_rate
                    : selectedReport.work_type_rate;
                  setEditData({ 
                    ...editData, 
                    rateType: newRateType,
                    rateUsed: newRate || editData.rateUsed
                  });
                }}
              >
                {selectedReport.employee_personal_rate && (
                  <FormControlLabel
                    value="personal"
                    control={<Radio />}
                    label={`${t('reports.personalRate')}: ${selectedReport.employee_personal_rate} UAH/hr`}
                  />
                )}
                {selectedReport.work_type_rate && (
                  <FormControlLabel
                    value="workType"
                    control={<Radio />}
                    label={`${t('reports.workTypeRate')}: ${selectedReport.work_type_rate} UAH/hr`}
                  />
                )}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('reports.amountRecalculated')}
              </Typography>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              label={t('reports.rateUsed')}
              type="number"
              value={editData.rateUsed}
              onChange={(e) =>
                setEditData({ ...editData, rateUsed: parseFloat(e.target.value) })
              }
              sx={{ mt: 2 }}
            />
          )}
          <TextField
            fullWidth
            label={t('reports.bonus')}
            type="number"
            value={editData.bonus}
            onChange={(e) =>
              setEditData({ ...editData, bonus: parseFloat(e.target.value) })
            }
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('reports.adminComment')}
            value={editData.adminComment}
            onChange={(e) =>
              setEditData({ ...editData, adminComment: e.target.value })
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsList;

